import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import OverviewTab from './OverviewTab';
import AssetsTab from './AssetsTab';
import FindingsTab from './FindingsTab';
import AttackGraphTab from './AttackGraphTab';
import ConnectorsTab from './ConnectorsTab';
import ScanHistoryTab from './ScanHistoryTab';
import MonitoringTab from './MonitoringTab';
import ComplianceImpactTab from './ComplianceImpactTab';
import CvePlaybookTab from './CvePlaybookTab';
import BasTab from './BasTab';
import CreditsTab from './CreditsTab';
import AdminCreditsPanel from './AdminCreditsPanel';
import axios from '../../api';

function getCallerRole() {
  return {
    role:    localStorage.getItem('role') || '',
    orgRole: localStorage.getItem('orgRole') || '',
  };
}

function needsOrgSelector(role, orgRole) {
  if (role === 'ROLE_PLATFORM_ADMIN') return true;
  if (orgRole === 'CISO_RESELLER' || orgRole === 'CPA_ADMIN' || orgRole === 'CPA_AUDITOR') return true;
  return false;
}

export default function InternalAsmDashboard({ forcedOrg } = {}) {
  const { t } = useTranslation();
  const TABS = [
    { id: 'overview',    label: t('InternalAsmDashboard.tabOverview'),         icon: 'fa-tachometer-alt' },
    { id: 'assets',      label: t('InternalAsmDashboard.tabAssets'),           icon: 'fa-server' },
    { id: 'findings',    label: t('InternalAsmDashboard.tabFindings'),         icon: 'fa-exclamation-triangle' },
    { id: 'graph',       label: t('InternalAsmDashboard.tabAttackGraph'),      icon: 'fa-project-diagram' },
    { id: 'bas',         label: t('InternalAsmDashboard.tabBas'),              icon: 'fa-flask' },
    { id: 'connectors',  label: t('InternalAsmDashboard.tabConnectors'),       icon: 'fa-plug' },
    { id: 'scans',       label: t('InternalAsmDashboard.tabScanHistory'),      icon: 'fa-history' },
    { id: 'monitoring',  label: t('InternalAsmDashboard.tabMonitoring'),       icon: 'fa-bell' },
    { id: 'compliance',  label: t('InternalAsmDashboard.tabComplianceImpact'), icon: 'fa-shield-alt' },
    { id: 'cve-playbooks', label: t('InternalAsmDashboard.tabCvePlaybooks'),   icon: 'fa-bullseye' },
    { id: 'credits',     label: t('InternalAsmDashboard.tabCredits'),          icon: 'fa-coins' },
  ];
  const location = useLocation();
  const deepLinkParams = new URLSearchParams(location.search);
  const deepLinkTab       = deepLinkParams.get('tab');
  const deepLinkFindingId = deepLinkParams.get('findingId');

  const [activeTab, setActiveTab] = useState(deepLinkTab || 'overview');
  const [deepFindingId, setDeepFindingId] = useState(
    deepLinkFindingId ? Number(deepLinkFindingId) : null
  );
  const [stats, setStats]             = useState(null);
  const [bootstrapping, setBootstrapping] = useState(false);
  const [bootstrapMsg, setBootstrapMsg]   = useState(null);
  // Orgs we've already auto-bootstrapped this mount — prevents an endless bootstrap
  // retry loop for orgs that have no seedable assets (created:0 leaves totalAssets at 0).
  const bootstrapAttempted = useRef(new Set());
  const [creditBalance, setCreditBalance] = useState(null);

  // Org context
  const { role, orgRole } = getCallerRole();
  const isPlatformAdmin   = role === 'ROLE_PLATFORM_ADMIN';
  // When forcedOrg is supplied (e.g. embedded in CPA client workspace), skip the
  // built-in selector entirely and pin to that org.
  const showOrgSelector   = !forcedOrg && needsOrgSelector(role, orgRole);
  const [orgContext, setOrgContext]   = useState(forcedOrg ? { id: forcedOrg.id, name: forcedOrg.name } : null);
  const [orgList, setOrgList]        = useState([]);
  const [orgListLoading, setOrgListLoading] = useState(false);

  // Load the accessible org list for platform admin / partner roles
  useEffect(() => {
    if (!showOrgSelector) return;
    setOrgListLoading(true);
    axios.get('/iasm/admin/orgs')
      .then(r => {
        setOrgList(r.data);
        // Pre-select: reseller may already have a client context from MyClients
        const partnerCtxId   = localStorage.getItem('partnerContextOrgId');
        const partnerCtxName = localStorage.getItem('partnerContextOrgName');
        if (partnerCtxId) {
          setOrgContext({ id: Number(partnerCtxId), name: partnerCtxName || `Org ${partnerCtxId}` });
        } else if (r.data.length > 0) {
          setOrgContext({ id: r.data[0].id, name: r.data[0].name });
        }
      })
      .catch(() => {})
      .finally(() => setOrgListLoading(false));
  }, [showOrgSelector]);

  // For org-scoped users, show their own org name in the header (read-only)
  const ownOrgName = localStorage.getItem('organization') || '';

  // orgId to pass to all child tabs and API calls
  const orgId = forcedOrg ? forcedOrg.id : (showOrgSelector ? orgContext?.id : undefined);

  const loadStats = useCallback(() => {
    const params = orgId ? { orgId } : {};
    axios.get('/iasm/stats', { params }).then(r => setStats(r.data)).catch(() => {});
  }, [orgId]);

  const loadCreditBalance = useCallback(() => {
    if (isPlatformAdmin) return; // admin doesn't buy credits
    const params = orgId ? { orgId } : {};
    axios.get('/iasm/credits/balance', { params })
      .then(r => setCreditBalance(r.data.credits))
      .catch(() => {});
  }, [orgId, isPlatformAdmin]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadCreditBalance(); }, [loadCreditBalance]);

  // Auto-bootstrap when org has no assets yet
  useEffect(() => {
    if (stats !== null && stats.totalAssets === 0 && !bootstrapping) {
      const orgKey = orgId ?? '__self__';
      // Only attempt once per org: bootstrap is idempotent and, for an org with no
      // seedable assets, returns created:0 without changing totalAssets — re-running
      // it on every render just spams POST /iasm/bootstrap (429-throttled → FAIL storm).
      if (bootstrapAttempted.current.has(orgKey)) return;
      bootstrapAttempted.current.add(orgKey);
      setBootstrapping(true);
      const params = orgId ? { orgId } : {};
      axios.post('/iasm/bootstrap', null, { params })
        .then(r => {
          if (r.data.created > 0) {
            return axios.post('/iasm/audit/run', null, { params }).then(audit => {
              setBootstrapMsg(`${r.data.message} ${t('InternalAsmDashboard.auditFoundFindings', { count: audit.data.totalNewFindings })}`);
              loadStats();
            });
          } else {
            setBootstrapMsg(r.data.message);
          }
        })
        .catch(() => {})
        .finally(() => setBootstrapping(false));
    }
  }, [stats, bootstrapping, loadStats, orgId, t]);

  // Reset tabs and stats whenever the selected org changes
  useEffect(() => {
    setStats(null);
    setBootstrapMsg(null);
    setActiveTab('overview');
    setCreditBalance(null);
  }, [orgContext?.id]);

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 600 }}>{t('InternalAsmDashboard.title')}</h2>
          <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: '14px' }}>
            {t('InternalAsmDashboard.subtitle')}
          </p>
        </div>

        {/* Org context bar */}
        {showOrgSelector ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px 14px' }}>
            <i className="fas fa-building" style={{ color: '#6b7280', fontSize: '13px' }} />
            <span style={{ fontSize: '13px', color: '#374151', fontWeight: 500 }}>{t('InternalAsmDashboard.organisationLabel')}</span>
            {orgListLoading ? (
              <span style={{ fontSize: '13px', color: '#9ca3af' }}>{t('InternalAsmDashboard.loading')}</span>
            ) : (
              <select
                value={orgContext?.id ?? ''}
                onChange={e => {
                  const selected = orgList.find(o => String(o.id) === e.target.value);
                  setOrgContext(selected ? { id: selected.id, name: selected.name } : null);
                }}
                style={{ border: '1px solid #d1d5db', borderRadius: '6px', padding: '5px 10px', fontSize: '13px', fontWeight: 600, background: '#fff', minWidth: '180px', color: '#111827' }}
              >
                {orgList.length === 0 && <option value="">{t('InternalAsmDashboard.noOrganisationsFound')}</option>}
                {orgList.map(o => (
                  <option key={o.id} value={String(o.id)}>{o.name}</option>
                ))}
              </select>
            )}
            {role === 'ROLE_PLATFORM_ADMIN' && (
              <span style={{ fontSize: '11px', padding: '2px 7px', background: '#fef3c7', color: '#92400e', borderRadius: '4px', fontWeight: 600 }}>
                {t('InternalAsmDashboard.adminView')}
              </span>
            )}
            {(orgRole === 'CISO_RESELLER' || orgRole === 'CPA_ADMIN' || orgRole === 'CPA_AUDITOR') && (
              <span style={{ fontSize: '11px', padding: '2px 7px', background: '#eff6ff', color: '#1d4ed8', borderRadius: '4px', fontWeight: 600 }}>
                {orgRole.replace('_', ' ')}
              </span>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px 14px' }}>
            <i className="fas fa-building" style={{ color: '#6b7280', fontSize: '13px' }} />
            <span style={{ fontSize: '13px', color: '#374151', fontWeight: 600 }}>{ownOrgName}</span>
          </div>
        )}

        {/* Credit balance chip — shown for non-platform-admin users */}
        {!isPlatformAdmin && creditBalance !== null && (
          <button
            onClick={() => setActiveTab('credits')}
            title={creditBalance < 0
              ? t('InternalAsmDashboard.creditTooltipBlocked')
              : creditBalance === 0
              ? t('InternalAsmDashboard.creditTooltipGrace')
              : t('InternalAsmDashboard.creditTooltipRemaining')}
            style={{
              display: 'flex', alignItems: 'center', gap: '7px',
              padding: '8px 14px', borderRadius: '8px', border: 'none', cursor: 'pointer',
              background: creditBalance <= 0 ? '#fee2e2' : creditBalance < 10 ? '#fff7ed' : '#f0fdf4',
              color: creditBalance <= 0 ? '#dc2626' : creditBalance < 10 ? '#d97706' : '#15803d',
            }}
          >
            <i className="fas fa-coins" style={{ fontSize: '13px' }} />
            <span style={{ fontSize: '13px', fontWeight: 700 }}>{creditBalance}</span>
            <span style={{ fontSize: '12px', fontWeight: 400 }}>{t('InternalAsmDashboard.credits')}</span>
            {creditBalance < 0
              ? <span style={{ fontSize: '11px', fontWeight: 600 }}>{t('InternalAsmDashboard.creditChipBlocked')}</span>
              : creditBalance === 0 && <span style={{ fontSize: '11px', fontWeight: 600 }}>{t('InternalAsmDashboard.creditChipBuyNow')}</span>}
          </button>
        )}
      </div>

      {/* Guard: admin/reseller must select an org before proceeding */}
      {showOrgSelector && !orgContext && (
        <div style={{ textAlign: 'center', padding: '60px 24px', background: '#f9fafb', border: '1px dashed #d1d5db', borderRadius: '12px' }}>
          <i className="fas fa-building" style={{ fontSize: '40px', color: '#d1d5db', marginBottom: '16px', display: 'block' }} />
          <p style={{ fontSize: '16px', fontWeight: 600, color: '#374151', margin: '0 0 8px' }}>{t('InternalAsmDashboard.selectOrgTitle')}</p>
          <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
            {t('InternalAsmDashboard.selectOrgBody')}
          </p>
        </div>
      )}

      {/* Dashboard content — only shown when org is resolved */}
      {(!showOrgSelector || orgContext) && (
        <>
          {bootstrapping && (
            <div style={{ marginBottom: '16px', padding: '12px 16px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', color: '#1d4ed8' }}>
              <i className="fas fa-spinner fa-spin" />
              {t('InternalAsmDashboard.bootstrapImporting')}
            </div>
          )}

          {bootstrapMsg && !bootstrapping && (
            <div style={{ marginBottom: '16px', padding: '12px 16px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', color: '#15803d' }}>
              <i className="fas fa-check-circle" />
              {bootstrapMsg}
            </div>
          )}

          {stats && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
              <StatCard label={t('InternalAsmDashboard.statTotalAssets')}  value={stats.totalAssets}           icon="fa-server"               color="#3b82f6" />
              <StatCard label={t('InternalAsmDashboard.statGraphEdges')}   value={stats.totalEdges}            icon="fa-project-diagram"      color="#8b5cf6" />
              <StatCard label={t('InternalAsmDashboard.statOpenFindings')} value={stats.openFindings}          icon="fa-exclamation-triangle" color="#ef4444" />
              <StatCard label={t('InternalAsmDashboard.statRecentScans')}  value={stats.recentScans?.length ?? 0} icon="fa-sync"             color="#10b981" />
            </div>
          )}

          <div style={{ borderBottom: '1px solid #e5e7eb', marginBottom: '24px', overflowX: 'auto' }}>
            <div style={{ display: 'flex', gap: '0', minWidth: 'max-content' }}>
              {TABS.filter(tab => tab.id !== 'credits' || !isPlatformAdmin).map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    padding: '10px 18px', border: 'none', background: 'none', cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: activeTab === tab.id ? 600 : 400,
                    color: activeTab === tab.id ? '#2563eb' : '#6b7280',
                    borderBottom: activeTab === tab.id ? '2px solid #2563eb' : '2px solid transparent',
                    display: 'flex', alignItems: 'center', gap: '5px', whiteSpace: 'nowrap',
                  }}
                >
                  <i className={`fas ${tab.icon}`} style={{ fontSize: '12px' }} />
                  {tab.label}
                  {tab.id === 'credits' && creditBalance !== null && creditBalance <= 0 && (
                    <span style={{ fontSize: '9px', fontWeight: 700, background: '#dc2626', color: '#fff', padding: '1px 5px', borderRadius: '3px', marginLeft: '2px' }}>{creditBalance < 0 ? '!' : '0'}</span>
                  )}
                </button>
              ))}
              {isPlatformAdmin && (
                <button
                  onClick={() => setActiveTab('admin-credits')}
                  style={{
                    padding: '10px 18px', border: 'none', background: 'none', cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: activeTab === 'admin-credits' ? 600 : 400,
                    color: activeTab === 'admin-credits' ? '#d97706' : '#6b7280',
                    borderBottom: activeTab === 'admin-credits' ? '2px solid #d97706' : '2px solid transparent',
                    display: 'flex', alignItems: 'center', gap: '5px', whiteSpace: 'nowrap',
                  }}
                >
                  <i className="fas fa-coins" style={{ fontSize: '12px' }} />
                  {t('InternalAsmDashboard.iasmCredits')}
                </button>
              )}
            </div>
          </div>

          <div>
            {activeTab === 'overview'       && <OverviewTab stats={stats} orgId={orgId} />}
            {activeTab === 'assets'         && <AssetsTab orgId={orgId} />}
            {activeTab === 'findings'       && <FindingsTab orgId={orgId} defaultExpandedId={deepFindingId} onDeepLinkConsumed={() => setDeepFindingId(null)} />}
            {activeTab === 'graph'          && <AttackGraphTab orgId={orgId} />}
            {activeTab === 'bas'            && <BasTab orgId={orgId} />}
            {activeTab === 'connectors'     && <ConnectorsTab onSyncComplete={loadStats} orgId={orgId} />}
            {activeTab === 'scans'          && <ScanHistoryTab orgId={orgId} />}
            {activeTab === 'monitoring'     && <MonitoringTab orgId={orgId} />}
            {activeTab === 'compliance'     && <ComplianceImpactTab orgId={orgId} />}
            {activeTab === 'cve-playbooks'  && <CvePlaybookTab orgId={orgId} />}
            {activeTab === 'credits'        && <CreditsTab orgId={orgId} />}
            {activeTab === 'admin-credits'  && <AdminCreditsPanel />}
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, icon, color }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '20px', display: 'flex', alignItems: 'center', gap: '16px' }}>
      <div style={{ width: '44px', height: '44px', borderRadius: '8px', background: color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <i className={`fas ${icon}`} style={{ color, fontSize: '18px' }} />
      </div>
      <div>
        <div style={{ fontSize: '24px', fontWeight: 700, color: '#111827' }}>{value ?? '—'}</div>
        <div style={{ fontSize: '13px', color: '#6b7280' }}>{label}</div>
      </div>
    </div>
  );
}
